import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "../utils";
import {
  RealEstate,
  RealEstateUpdateRequest,
  RealEstateCreateRequest,
} from "../../../../api/src/types";

interface BasePathParams {
  userId: string;
  docId: string;
}

// linter takes away this const

// eslint-disable-next-line
const realEstatesTagType = "real-estates" as const;

export const realEstatesApi = createApi({
  reducerPath: "realEstatesApi",
  baseQuery,
  tagTypes: [realEstatesTagType],
  endpoints: (builder) => ({
    getRealEstate: builder.query<RealEstate, BasePathParams & { id: string }>({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/real-estates/${id}`,
        method: "GET",
      }),

      providesTags: (_, __, query) => [
        { type: realEstatesTagType, id: query.id },
      ],
    }),
    getRealEstatesByUserId: builder.query<RealEstate[], BasePathParams>({
      query: ({ userId, docId }) => {
        return `users/${userId}/documents/${docId}/real-estates`;
      },
      merge: (currentCache, newItems) => {
        const itemMap = new Map(currentCache.map((item) => [item.id, item]));
        newItems.forEach((item) => itemMap.set(item.id, item));
        return Array.from(itemMap.values());
      },

      providesTags: (result) => [
        ...(result?.map((realEstate) => ({
          type: realEstatesTagType,
          id: realEstate.id,
        })) ?? []),
        { type: realEstatesTagType, id: "LIST" },
      ],
    }),

    updateRealEstate: builder.mutation<
      RealEstate,
      BasePathParams & { id: string; data: RealEstateUpdateRequest }
    >({
      query: ({ userId, docId, id, data }) => ({
        url: `users/${userId}/documents/${docId}/real-estates/${id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (_, __, { id }) => [{ type: realEstatesTagType, id }],
    }),

    createRealEstate: builder.mutation<
      RealEstate,
      BasePathParams & { data: RealEstateCreateRequest }
    >({
      query: ({ userId, docId, data }) => ({
        url: `users/${userId}/documents/${docId}/real-estates`,
        method: "POST",
        body: data,
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: newItem } = await queryFulfilled;
          if (newItem.userId && newItem.id && newItem.documentId) {
            dispatch(
              realEstatesApi.util.updateQueryData(
                "getRealEstatesByUserId",
                { userId: newItem.userId, docId: newItem.documentId },
                (draft: RealEstate[]) => {
                  draft.push(newItem);
                },
              ),
            );
          }
        } catch (error) {
          console.log("rtk query createRealEstate error", error);
        }
      },
    }),
    deleteRealEstate: builder.mutation<
      RealEstate,
      BasePathParams & { id: string }
    >({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/real-estates/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(
        { userId, docId, id },
        { dispatch, queryFulfilled },
      ) {
        await queryFulfilled;
        dispatch(
          realEstatesApi.util.updateQueryData(
            "getRealEstatesByUserId",
            { userId, docId },
            (draft: RealEstate[]) => {
              return draft.filter((item) => item.id !== id);
            },
          ),
        );
      },
    }),
  }),
});

export const {
  useGetRealEstateQuery,
  useGetRealEstatesByUserIdQuery,
  useUpdateRealEstateMutation,
  useCreateRealEstateMutation,
  useDeleteRealEstateMutation,
} = realEstatesApi;
